import { TableRowValueList } from '@vinnova/ui';
import { Observable } from 'rxjs';
import { UpparbetadeKostnaderImportClient } from '../../../../core/clients/upparbetade-kostnader-import.client';
import { ExcelImportModalBindings } from '../excel-import-modal/excel-import-modal.model';
import { UpparbetadeKostnaderImportHelper } from './upparbetade-kostnader-import.helper';
import {
  UpparbetadeKostnaderImportProjektpart,
  UpparbetadeKostnaderImportResult,
  UpparbetadeKostnaderSlotSchema,
} from './upparbetade-kostnader-import.interface';
import { UPPARBETADE_KOSTNADER_IMPORT_TEXT } from './upparbetade-kostnader-import.text';

/** Wires UpparbetadeKostnaderSlotSchema/UpparbetadeKostnaderImportClient into ExcelImportModalShellComponent's initialState. */
export function buildUpparbetadeKostnaderImportBindings(
  schema: UpparbetadeKostnaderSlotSchema,
  currentTables: Map<string, TableRowValueList>,
  rapportId: string,
  client: UpparbetadeKostnaderImportClient,
): ExcelImportModalBindings {
  return {
    texts: UPPARBETADE_KOSTNADER_IMPORT_TEXT,
    years: schema.perioder.map((p) => p.ar),
    // A period rarely covers the whole calendar year (e.g. a project's first/last year), so show the
    // actual start–end dates instead of a bare year that would hide that.
    columnLabels: Object.fromEntries(
      schema.perioder.map((p) => [p.ar, `${p.startDate} – ${p.endDate}`]),
    ),
    deploymentItems: [
      { value: 'excel-test', label: 'gpt-5' },
      { value: 'Mistral-Large-3', label: 'Mistral-Large-3' },
      { value: 'gpt-5.4-mini', label: 'gpt-5.4-mini' },
      { value: 'gpt-5.6-luna', label: 'gpt-5.6-luna' },
    ],
    defaultDeployment: 'gpt-5.6-luna',
    supportsPreprocessing: true,
    supportsDebugToggle: true,
    analyzeFn: (file, options) =>
      client.importUpparbetadeKostnader(
        rapportId,
        file,
        schema,
        options.reasoningEffort,
        options.deployment,
        options.preprocessing,
      ) as Observable<unknown>,
    buildPreviewFn: (result, unit) =>
      UpparbetadeKostnaderImportHelper.buildPreview(
        result as UpparbetadeKostnaderImportResult,
        unit,
        currentTables,
      ).map((p) => ({
        part: p.part,
        hasImportedValues: p.hasImportedValues,
        sections: [
          {
            title: UPPARBETADE_KOSTNADER_IMPORT_TEXT.kostnader,
            rows: p.kostnader,
          },
        ],
      })),
    getUnitFn: (result) =>
      (result as UpparbetadeKostnaderImportResult).unit ?? 'SEK',
    getUnmatchedBlocksFn: (result) =>
      (result as UpparbetadeKostnaderImportResult).unmatchedBlocks,
    getWarningsFn: (result) =>
      (result as UpparbetadeKostnaderImportResult).warnings,
    getSummaryFn: (result) =>
      UpparbetadeKostnaderImportHelper.buildSummary(
        result as UpparbetadeKostnaderImportResult,
      ),
    getReconciliationFn: (result, partId, unit) =>
      UpparbetadeKostnaderImportHelper.buildReconciliation(
        result as UpparbetadeKostnaderImportResult,
        partId,
        unit,
      ),
    getExcludedFn: (result, unit) =>
      UpparbetadeKostnaderImportHelper.buildExcluded(
        result as UpparbetadeKostnaderImportResult,
        unit,
      ),
    getTimingsFn: (result) =>
      (result as UpparbetadeKostnaderImportResult).timings,
    toApplyResultFn: (includedParts) => ({
      projektparter: UpparbetadeKostnaderImportHelper.toApplyParts(
        includedParts.map((p) => ({
          part: p.part as UpparbetadeKostnaderImportProjektpart,
          // row.id is the rowType as a string (see UpparbetadeKostnaderImportHelper.buildRow), restore it here.
          kostnader: (p.sections[0]?.rows ?? []).map((row) => ({
            ...row,
            rowType: Number(row.id),
          })),
          hasImportedValues: p.hasImportedValues,
        })),
      ),
    }),
  };
}
